import { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Phone, PhoneIncoming, PhoneDisconnect, CheckCircle, XCircle, MinusCircle, Timer } from "@phosphor-icons/react";
import { CALL_OUTCOMES, hms, ukTime } from "./oohUtils";

function LiveTimer({ since }) {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 1000); return () => clearInterval(t); }, []);
  return <span className="font-mono tabular-nums" data-testid="ooh-call-timer">{hms((Date.now() - new Date(since).getTime()) / 1000)}</span>;
}

function QuestionRow({ c, q, onChanged }) {
  const existing = (c.answers || []).find((a) => a.key === q.key);
  const [val, setVal] = useState(existing?.answer || "");
  useEffect(() => { setVal(existing?.answer || ""); }, [existing?.answer]);
  const save = async () => {
    if ((existing?.answer || "") === val.trim()) return;
    try { await API.post(`/ooh/cases/${c.id}/answers`, { key: q.key, text: q.text, answer: val.trim() }); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save the answer"); }
  };
  return (
    <li className="grid sm:grid-cols-[1fr_200px] gap-2 items-start" data-testid={`ooh-question-${q.key}`}>
      <div>
        <p className="text-sm">{q.text}</p>
        {q.why && <p className="text-[11px] text-muted-foreground">{q.why}</p>}
      </div>
      <Input value={val} onChange={(e) => setVal(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} placeholder="Answer" className={`h-9 text-sm ${existing ? "border-emerald-400" : ""}`} data-testid={`ooh-answer-${q.key}`} />
    </li>
  );
}

function StepRow({ c, s, idx, onChanged, disabled }) {
  const logged = (c.step_log || []).find((l) => l.title === s.title);
  const [note, setNote] = useState("");
  const mark = async (result) => {
    try { await API.post(`/ooh/cases/${c.id}/steps`, { index: idx, title: s.title, result, note }); setNote(""); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't log the step"); }
  };
  const tone = logged?.result === "done" ? "border-emerald-400 bg-emerald-50/60 dark:bg-emerald-950/20" : logged?.result === "failed" ? "border-red-300 bg-red-50/60 dark:bg-red-950/20" : logged?.result === "skipped" ? "border-border opacity-70" : "border-border";
  return (
    <li className={`rounded-md border px-3 py-2 ${tone}`} data-testid={`ooh-step-${idx}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium"><span className="text-muted-foreground mr-1">{idx + 1}.</span>{s.title} <span className={`ml-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${s.type === "make_safe" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"}`}>{s.type === "make_safe" ? "make safe" : "self-help"}</span></p>
          {s.detail && <p className="text-sm text-muted-foreground mt-0.5">{s.detail}</p>}
          {s.check && <p className="text-[11px] text-emerald-700 dark:text-emerald-300 mt-0.5">✓ Worked if: {s.check}</p>}
          {logged && <p className="text-[11px] mt-1 font-medium" data-testid={`ooh-step-result-${idx}`}>{logged.result === "done" ? "Done" : logged.result === "failed" ? "Didn't work" : "Skipped"}{logged.note ? ` — ${logged.note}` : ""} · {logged.by}</p>}
        </div>
        {!disabled && (
          <div className="flex items-center gap-1 shrink-0">
            <button type="button" title="Done — it worked" onClick={() => mark("done")} className="p-1 rounded hover:bg-emerald-100 text-emerald-600" data-testid={`ooh-step-done-${idx}`}><CheckCircle size={20} weight={logged?.result === "done" ? "fill" : "regular"} /></button>
            <button type="button" title="Didn't work" onClick={() => mark("failed")} className="p-1 rounded hover:bg-red-100 text-red-600" data-testid={`ooh-step-failed-${idx}`}><XCircle size={20} weight={logged?.result === "failed" ? "fill" : "regular"} /></button>
            <button type="button" title="Skip" onClick={() => mark("skipped")} className="p-1 rounded hover:bg-muted text-muted-foreground" data-testid={`ooh-step-skip-${idx}`}><MinusCircle size={20} weight={logged?.result === "skipped" ? "fill" : "regular"} /></button>
          </div>
        )}
      </div>
      {!disabled && <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note before you tick it (e.g. stopcock was stiff, got it off)" className="h-7 text-[11px] mt-2" data-testid={`ooh-step-note-${idx}`} />}
    </li>
  );
}

export default function OohCallPanel({ c, onChanged, onEscalate }) {
  const ai = c.ai_triage;
  const open = c.open_call;
  const closed = c.status === "closed";
  const [busy, setBusy] = useState(false);
  const [end, setEnd] = useState({ open: false, outcome: "made_safe", notes: "" });

  const start = async (direction) => {
    setBusy(true);
    try { await API.post(`/ooh/cases/${c.id}/calls/start`, { direction }); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start the call"); }
    finally { setBusy(false); }
  };
  const finish = async () => {
    setBusy(true);
    try {
      await API.post(`/ooh/cases/${c.id}/calls/${open.id}/end`, { outcome: end.outcome, notes: end.notes });
      toast.success(end.outcome === "needs_callout" ? "Call logged — now pick the SLA and contractor" : "Call logged");
      if (end.outcome === "needs_callout") setTimeout(() => onEscalate?.(), 150);
      setEnd({ open: false, outcome: "made_safe", notes: "" });
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't end the call"); }
    finally { setBusy(false); }
  };

  const total = (c.calls || []).reduce((n, x) => n + (x.duration_seconds || 0), 0);
  return (
    <section className="rounded-md border border-border bg-card" data-testid="ooh-call-panel">
      <header className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b border-border/70">
        <h3 className="text-sm font-semibold flex items-center gap-2"><Phone size={16} weight="duotone" className="text-primary" /> Call script & steps</h3>
        <div className="flex items-center gap-2 text-xs">
          {c.calls?.length > 0 && <span className="text-muted-foreground inline-flex items-center gap-1"><Timer size={12} /> {c.calls.length} call{c.calls.length === 1 ? "" : "s"} · {hms(total)}</span>}
          {open ? (
            <>
              <span className="inline-flex items-center gap-1 text-red-600 font-semibold"><span className="h-2 w-2 rounded-full bg-red-600 animate-pulse" /> On call <LiveTimer since={open.started_at} /></span>
              <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => setEnd({ open: true, outcome: c.urgency === "urgent" ? "needs_callout" : "made_safe", notes: "" })} data-testid="ooh-call-end"><PhoneDisconnect size={13} /> End call</Button>
            </>
          ) : !closed && (
            <>
              <Button size="sm" className="h-7 text-xs" onClick={() => start("outbound")} disabled={busy} data-testid="ooh-call-start"><Phone size={13} weight="fill" /> Call {c.requester?.phone || "requester"}</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => start("inbound")} disabled={busy} data-testid="ooh-call-start-inbound"><PhoneIncoming size={13} /> They rang in</Button>
            </>
          )}
        </div>
      </header>
      <div className="px-4 py-3 space-y-4">
        {ai?.questions?.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Ask</p>
            <ul className="space-y-2">{ai.questions.map((q) => <QuestionRow key={q.key} c={c} q={q} onChanged={onChanged} />)}</ul>
          </div>
        )}
        {ai?.steps?.length > 0 ? (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Walk them through</p>
            <ul className="space-y-2">{ai.steps.map((s, i) => <StepRow key={i} c={c} s={s} idx={i} onChanged={onChanged} disabled={closed} />)}</ul>
          </div>
        ) : ai && <p className="text-xs text-muted-foreground">No self-help steps suggested{ai.urgency === "urgent" ? " — this one needs a contractor tonight." : "."}</p>}
        {c.calls?.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Calls</p>
            <ul className="text-xs space-y-1" data-testid="ooh-calls-list">
              {c.calls.map((x) => (
                <li key={x.id} className="flex flex-wrap gap-x-3 text-muted-foreground">
                  <span className="font-mono">{ukTime(x.started_at)}</span>
                  <span>{x.direction === "inbound" ? "Inbound" : "Outbound"} · {x.by_name}</span>
                  <span>{x.ended_at ? hms(x.duration_seconds) : "in progress"}</span>
                  {x.outcome && <span className="text-foreground font-medium">{CALL_OUTCOMES.find(([k]) => k === x.outcome)?.[1] || x.outcome}</span>}
                  {x.notes && <span className="basis-full">{x.notes}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <Dialog open={end.open} onOpenChange={(o) => setEnd((e) => ({ ...e, open: o }))}>
        <DialogContent data-testid="ooh-end-call-dialog">
          <DialogHeader><DialogTitle>End call — what happened?</DialogTitle></DialogHeader>
          <div className="grid gap-2">
            {CALL_OUTCOMES.map(([k, l]) => (
              <label key={k} className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer ${end.outcome === k ? (k === "needs_callout" ? "border-red-500 bg-red-50 dark:bg-red-950/30" : "border-primary bg-primary/5") : "border-border"}`}>
                <input type="radio" name="ooh-outcome" value={k} checked={end.outcome === k} onChange={() => setEnd((e) => ({ ...e, outcome: k }))} data-testid={`ooh-outcome-${k}`} /> {l}
              </label>
            ))}
          </div>
          {end.outcome === "needs_callout" && <p className="text-xs text-red-700">Next you'll pick the SLA (2/4/6/8h) and the contractor — the Critical emergency job is raised from there.</p>}
          <Textarea rows={3} value={end.notes} onChange={(e) => setEnd((x) => ({ ...x, notes: e.target.value }))} placeholder="Call notes for the report" data-testid="ooh-end-call-notes" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnd({ open: false, outcome: "made_safe", notes: "" })}>Cancel</Button>
            <Button onClick={finish} disabled={busy} data-testid="ooh-end-call-confirm">{busy ? "Saving…" : "Log call"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
